import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { UrlProvider } from '../url';
import { Downloader, FileLink } from './abstract';

@Injectable()
export class DownloaderImpl implements Downloader {
	constructor(@Inject(DOCUMENT) readonly document: Document, readonly url: UrlProvider) {}

	getFileId(file: Record<any, any>) {
		return file?.id || file?.uploadId || file?.uploadID;
	}

	downloadAllFilesInComment(commentId: number): Observable<any> {
		const url = this.url.getApiUrl(`files/${commentId}/zip`);
		return this.download(url, `all_files_${commentId}`);
	}

	download(href: string, fileName?: string): Observable<any> {
		return new Observable(observer => {
			try {
				const link = this.document.createElement('a');
				link.style.display = 'none';
				link.href = href;
				if (fileName) {
					link.download = fileName;
				}
				this.document.body.appendChild(link);
				link.click();
				this.document.body.removeChild(link);
				observer.next(0 as any);
			} catch (err) {
				observer.error(err);
			} finally {
				observer.complete();
			}
		});
	}

	createDownloadLink(fileOrId: FileLink): string {
		if (typeof fileOrId === 'number' || typeof fileOrId === 'string') {
			if (!fileOrId) {
				return '';
			}

			return this.url.getUrl(`GetAttachment.ashx?id=${fileOrId}`);
		}

		const id = this.getFileId(fileOrId);
		const versionId = fileOrId.versionId || fileOrId.versionID;

		if (!id) {
			return '';
		}

		return this.url.getUrl(`GetAttachment.ashx?id=${id}&versionId=${versionId}`);
	}

	createPreviewPdfLink(fileOrId: FileLink): string {
		if (typeof fileOrId === 'number' || typeof fileOrId === 'string') {
			if (!fileOrId) {
				return '';
			}

			return this.url.getUrl(`PDFPreview.aspx?id=${fileOrId}`);
		}

		const id = this.getFileId(fileOrId);
		const versionId = fileOrId.versionId;

		if (!id) {
			return '';
		}

		return this.url.getUrl(`PDFPreview.aspx?id=${id}&VersionId=${versionId}&GetTrueMime=1`);
	}

	createPreviewOfficeWebAppLink(fileOrId: FileLink): string {
		if (typeof fileOrId === 'number' || typeof fileOrId === 'string') {
			if (!fileOrId) {
				return '';
			}

			return this.url.getUrl(`component/WAFrame.aspx?fileId=${fileOrId}`);
		}

		const id = this.getFileId(fileOrId);
		const versionId = fileOrId.versionId;

		if (!id) {
			return '';
		}

		return this.url.getUrl(`component/WAFrame.aspx?fileId=${id}&versionId=${versionId}`);
	}

	createPreviewWebDAVLink(fileOrId: FileLink, name: string): string {
		if (typeof fileOrId === 'number' || typeof fileOrId === 'string') {
			if (!fileOrId) {
				return '';
			}

			return this.url.getUrl(`/WebDav/${fileOrId}/No/${name}`);
		}

		const id = this.getFileId(fileOrId);
		const versionId = fileOrId.versionId;

		if (!id) {
			return '';
		}

		return this.url.getUrl(`/WebDav/${id}/No/${name}`);
	}

	createPreviewTextLink(fileOrId: FileLink): string {
		if (typeof fileOrId === 'number' || typeof fileOrId === 'string') {
			if (!fileOrId) {
				return '';
			}

			return this.url.getUrl(`TextFilePreview.aspx?id=${fileOrId}&GetTrueMime=1&addbr=1`);
		}

		const id = this.getFileId(fileOrId);
		const versionId = fileOrId.versionId;

		if (!id) {
			return '';
		}

		return this.url.getUrl(`TextFilePreview.aspx?id=${id}&VersionId=${versionId}&GetTrueMime=1&addbr=1`);
	}

	createPreviewImageLink(fileOrId: FileLink): string {
		if (typeof fileOrId === 'number' || typeof fileOrId === 'string') {
			if (!fileOrId) {
				return '';
			}

			return this.url.getUrl(`FilePreview.aspx?inRadWindow=1&id=${fileOrId}&GetTrueMime=1&addbr=1`);
		}

		const id = this.getFileId(fileOrId);
		const versionId = fileOrId.versionId;

		if (!id) {
			return '';
		}

		return this.url.getUrl(`FilePreview.aspx?inRadWindow=1&id=${id}&VersionId=${versionId}&GetTrueMime=1&addbr=1`);
	}
}
